import {UyapHelper} from "@/plugins/uyap-plugin/helper";
import {metinTemizle as metin} from "@/utils/metinNormalize";

const helper = new UyapHelper();

/**
 * Genel Islem Sihirbazi 2. adiminin TABLO FILTRESI. (DEV-2718)
 *
 * Buradaki fonksiyonlar saf: Vue'ya, bilesene ya da $uyap'a bagli degiller. Iki sebeple:
 *  1. "Ekranda gorunen satir kumesi" TEK bir yerden hesaplansin. Eskiden tablo kendi
 *     filtrelerinden, Vuetify'in :search prop'u ayri bir katmandan geciyordu; "tumunu sec"
 *     aramayi hic gormedigi icin kullanicinin gormedigi satirlar goreve donusuyordu.
 *  2. Mount etmeden test edilebilsin (bkz. tests/unit/eicrapro/genelIslemTakipFiltre.spec.ts).
 *
 * Sunucu sorgusu (1. adim, localValue.filtre) ile tablo filtresi (2. adim, localValue.tabloFiltre)
 * BILEREK ayri nesnelerde tutuluyor. Eskiden ayni nesneydiler; 2. adimda yazilan yil/sira bir
 * sonraki UYAP sorgusunu da degistiriyor, Excel yuklendiginde 1. adimdan kalan degerler
 * Excel satirlarina uygulanip listeyi sessizce bosaltiyordu.
 */

export type FiltreAnahtar = 'arama' | 'birim' | 'esasNo' | 'tarih' | 'dosyaDurum';

/**
 * DIKKAT: her alanin ACIK bir baslangic degeri olmali. Proje ts-loader + TS 3.8.3 ile
 * derleniyor (target ES2020, useDefineForClassFields kapali); initializer'i olmayan class
 * alani derlenmis ciktida hic olusmuyor, dolayisiyla Vue 2 onu reaktif yapamiyor ve filtre
 * degisikligi tabloya yansimiyor. (DEV-2721)
 */
export class TakipTabloFiltreEntity {
    arama: string = "";
    /** Coklu secim: kullanici birden fazla icra dairesini ayni anda kapsama alabiliyor. */
    birimIdler: Array<string> = [];
    dosyaYil: string = "";
    dosyaSira: string = "";
    dosyaSira2: string = "";
    baslangicTarihi: string = "";
    bitisTarihi: string = "";
    dosyaDurumlar: Array<string> = [];
}

export interface FiltreAdimi {
    anahtar: FiltreAnahtar;
    /** Kullaniciya gosterilecek kisa aciklama: "Mahkeme: Ankara 3. Icra Dairesi" */
    etiket: string;
    /** Bu filtrenin eledigi satir sayisi. Hicbir filtre sessiz kalmamali. */
    elenen: number;
    /** Elemenin dikkat gerektiren tarafi varsa aciklamasi. */
    not?: string;
}

export interface GorunurTakipSonucu {
    satirlar: Array<any>;
    adimlar: Array<FiltreAdimi>;
    toplam: number;
}

export interface SecenekSatiri {
    deger: string;
    metin: string;
}

// kod inceleme: gövde excelDosyaCozumleme.ts'teki metinTemizle() ile birebir
// aynıydı (iki bağımsız kopya); paylaşılan @/utils/metinNormalize'a taşındı,
// burada yalnız mevcut yerel `metin` adıyla kullanılıyor.

/** Coklu secim alanlarini karsilastirmaya hazir kumeye cevirir; tekil deger de kabul eder. */
function secilenKume(deger: any): Set<string> {
    const dizi = Array.isArray(deger) ? deger : [deger];
    return new Set(dizi.map(metin).filter(d => d !== ""));
}

function sayi(deger: any): number | null {
    const temiz = metin(deger);
    if (temiz === "")
        return null;
    const n = Number(temiz);
    return isNaN(n) ? null : n;
}

/** Esas no ayristirma TEK KAYNAKTAN gelir; bkz. helper.esasNoParcalari. (DEV-2722) */
export function esasNoParcalari(dosyaNo: any): Array<{ yil: number, sira: number }> {
    return helper.esasNoParcalari(dosyaNo);
}

export function tarihMetni(dosyaAcilisTarihi: any): string {
    if (!dosyaAcilisTarihi)
        return "";
    try {
        return helper.tarihLocaleStr(dosyaAcilisTarihi);
    } catch (e) {
        return "";
    }
}

/**
 * UYAP tarihi hem {date:{...},time:{...}} nesnesi hem duz string olarak gelebiliyor.
 * Cozulemezse null doner; cagiran taraf satiri ELEMEZ, "tarihi bilinmiyor" sayar.
 */
export function tariheCevir(tarih: any): Date | null {
    if (!tarih)
        return null;
    try {
        if (tarih instanceof Date)
            return isNaN(tarih.getTime()) ? null : tarih;
        if (tarih.date && tarih.time) {
            const d = new Date(
                tarih.date.year,
                tarih.date.month - 1,
                tarih.date.day,
                tarih.time.hour || 0,
                tarih.time.minute || 0,
                tarih.time.second || 0
            );
            return isNaN(d.getTime()) ? null : d;
        }
        const d = new Date(typeof tarih === "string" ? tarih : String(tarih));
        return isNaN(d.getTime()) ? null : d;
    } catch (e) {
        return null;
    }
}

/** Tablonun tum kolonlarinin aranabilir metni. Tarih kolonu ekranda goruldugu bicimde aranir. */
export function satirAramaMetni(takip: any): string {
    return [
        takip?.birimAdi,
        takip?.eslesenBirimAdi,
        takip?.dosyaNo,
        takip?.dosyaDurum,
        tarihMetni(takip?.dosyaAcilisTarihi)
    ].map(metin).filter(x => x !== "").join(" ");
}

export function applyAramaFiltresi(takipler: Array<any>, arama: any): Array<any> {
    const kelimeler = helper.turkishToLower(metin(arama)).split(" ").filter(k => k !== "");
    if (kelimeler.length === 0)
        return takipler;
    return takipler.filter((takip: any) => {
        const hedef = helper.turkishToLower(satirAramaMetni(takip));
        return kelimeler.every(k => hedef.indexOf(k) >= 0);
    });
}

/**
 * Icra dairesi filtresi. birimId'si cozulemeyen satirlar elenir -- tam da riskli olanlar
 * bunlar oldugu icin kac tanesinin elendigi ayrica raporlanir. (DEV-2724)
 */
export function applyBirimFiltresi(
    takipler: Array<any>,
    birimIdler: any
): { satirlar: Array<any>, cozulemeyen: number } {
    const secilenler = secilenKume(birimIdler);
    if (secilenler.size === 0)
        return {satirlar: takipler, cozulemeyen: 0};
    let cozulemeyen = 0;
    const satirlar = takipler.filter((takip: any) => {
        const takipBirimId = metin(takip?.birimId);
        if (takipBirimId === "") {
            cozulemeyen++;
            return false;
        }
        return secilenler.has(takipBirimId);
    });
    return {satirlar, cozulemeyen};
}

/**
 * Esas no filtresi: yil ve sira AYNI esas no ciftinde birlikte aranir.
 *  - tek sira degeri  -> TAM ESITLIK (eskiden includes() idi: "23" yazan kullanici
 *                        "2024/9123" dosyasini da goruyordu)
 *  - iki sira degeri  -> sayisal aralik
 * Birlesen dosyalarda ("2016/7232 - ( 2015/7070 )") her iki cift de denenir.
 * Yil+sira birlikte verilmisse ayni cifte bakilir; aksi halde gercekte var olmayan bir
 * yil/sira kombinasyonu eslesirdi. UYAP'in kendi esasNoAraligi sorgusu da boyle davraniyor.
 * (DEV-2722)
 */
export function applyEsasNoFiltresi(
    takipler: Array<any>,
    dosyaYil: any,
    dosyaSira: any,
    dosyaSira2: any
): Array<any> {
    const yil = sayi(dosyaYil);
    const sira1 = sayi(dosyaSira);
    const sira2 = sayi(dosyaSira2);
    if (yil === null && sira1 === null)
        return takipler;

    return takipler.filter((takip: any) => {
        const parcalar = esasNoParcalari(takip?.dosyaNo);
        if (parcalar.length === 0)
            return false;
        return parcalar.some(parca => {
            if (yil !== null && parca.yil !== yil)
                return false;
            if (sira1 === null)
                return true;
            if (sira2 === null)
                return parca.sira === sira1;
            return parca.sira >= Math.min(sira1, sira2) && parca.sira <= Math.max(sira1, sira2);
        });
    });
}

/**
 * Tarih filtresi. Acilis tarihi OLMAYAN satir ELENMEZ, "tarihi bilinmiyor" sayilip gecirilir
 * ve kac tane oldugu raporlanir. Excel ile yuklenen listede (ornek sablon yalnizca birimAdi ve
 * dosyaNo iceriyor) ve IcraPro Dosyalari listelemesinde tarih alani hic gelmiyor; eskiden bu
 * filtre listenin tamamini sessizce siliyordu. (DEV-2723)
 */
export function applyTarihFiltresi(
    takipler: Array<any>,
    baslangicTarihi: any,
    bitisTarihi: any
): { satirlar: Array<any>, tarihsiz: number } {
    const basMetin = metin(baslangicTarihi);
    const bitMetin = metin(bitisTarihi);
    if (basMetin === "" && bitMetin === "")
        return {satirlar: takipler, tarihsiz: 0};

    let baslangic: Date | null = null;
    let bitis: Date | null = null;
    if (basMetin !== "") {
        baslangic = tariheCevir(basMetin);
        if (baslangic)
            baslangic.setHours(0, 0, 0, 0);
    }
    if (bitMetin !== "") {
        bitis = tariheCevir(bitMetin);
        if (bitis)
            bitis.setHours(23, 59, 59, 999);
    }

    let tarihsiz = 0;
    const satirlar = takipler.filter((takip: any) => {
        const dosyaTarihi = tariheCevir(takip?.dosyaAcilisTarihi);
        if (!dosyaTarihi) {
            tarihsiz++;
            return true;
        }
        if (baslangic && dosyaTarihi < baslangic)
            return false;
        if (bitis && dosyaTarihi > bitis)
            return false;
        return true;
    });
    return {satirlar, tarihsiz};
}

export function applyDosyaDurumFiltresi(takipler: Array<any>, dosyaDurumlar: any): Array<any> {
    const secilenler = secilenKume(dosyaDurumlar);
    if (secilenler.size === 0)
        return takipler;
    return takipler.filter((takip: any) => secilenler.has(metin(takip?.dosyaDurum)));
}

/**
 * Satirlari geldikleri sirada tutar. Excel'de rowOrder, UYAP'ta index dolu oluyor.
 * Girdi dizisini DEGISTIRMEZ: filtreleme artik derin kopya uretmedigi icin yerinde
 * siralamak ham listenin sirasini bozardi.
 */
export function siralaOrijinal(takipler: Array<any>): Array<any> {
    return takipler.slice().sort((a: any, b: any) => {
        if (a?.rowOrder !== undefined && b?.rowOrder !== undefined)
            return a.rowOrder - b.rowOrder;
        if (a?.originalIndex !== undefined && b?.originalIndex !== undefined)
            return a.originalIndex - b.originalIndex;
        return (a?.index || 0) - (b?.index || 0);
    });
}

/**
 * Ekranda gorunecek satir kumesini hesaplar. Tablo, "tumunu sec", sayaclar, Excel indirme ve
 * gorev olusturma HEPSI bu tek sonuctan beslenir.
 *
 * Satir nesneleri KOPYALANMAZ: eskiden her hesaplamada JSON.parse(JSON.stringify(...)) ile
 * yeni nesneler uretiliyordu, secilen satirlar tabloda render edilenlerle ayni referans
 * olmuyordu ve Toplu* sihirbazlarin satir basina tuttugu hazirlik verisi kayboluyordu.
 */
export function hesaplaGorunurTakipler(
    takipler: Array<any> | undefined | null,
    filtre: TakipTabloFiltreEntity | any
): GorunurTakipSonucu {
    const adimlar: Array<FiltreAdimi> = [];
    if (!takipler || !Array.isArray(takipler) || takipler.length === 0)
        return {satirlar: [], adimlar, toplam: 0};

    const toplam = takipler.length;
    const f = filtre || new TakipTabloFiltreEntity();
    let satirlar = takipler.slice();

    if (secilenKume(f.birimIdler).size > 0) {
        const oncekiSayi = satirlar.length;
        const sonuc = applyBirimFiltresi(satirlar, f.birimIdler);
        satirlar = sonuc.satirlar;
        adimlar.push({
            anahtar: 'birim',
            etiket: 'Mahkeme',
            elenen: oncekiSayi - satirlar.length,
            not: sonuc.cozulemeyen > 0
                ? sonuc.cozulemeyen + " satırın icra dairesi çözülemediği için elendi"
                : undefined
        });
    }

    if (metin(f.dosyaYil) !== "" || metin(f.dosyaSira) !== "") {
        const oncekiSayi = satirlar.length;
        satirlar = applyEsasNoFiltresi(satirlar, f.dosyaYil, f.dosyaSira, f.dosyaSira2);
        adimlar.push({anahtar: 'esasNo', etiket: 'Esas No', elenen: oncekiSayi - satirlar.length});
    }

    if (metin(f.baslangicTarihi) !== "" || metin(f.bitisTarihi) !== "") {
        const oncekiSayi = satirlar.length;
        const sonuc = applyTarihFiltresi(satirlar, f.baslangicTarihi, f.bitisTarihi);
        satirlar = sonuc.satirlar;
        adimlar.push({
            anahtar: 'tarih',
            etiket: 'Açılış Tarihi',
            elenen: oncekiSayi - satirlar.length,
            not: sonuc.tarihsiz > 0
                ? sonuc.tarihsiz + " satırda açılış tarihi bilgisi olmadığı için bu filtre onlara uygulanmadı"
                : undefined
        });
    }

    if (secilenKume(f.dosyaDurumlar).size > 0) {
        const oncekiSayi = satirlar.length;
        satirlar = applyDosyaDurumFiltresi(satirlar, f.dosyaDurumlar);
        adimlar.push({anahtar: 'dosyaDurum', etiket: 'Dosya Durumu', elenen: oncekiSayi - satirlar.length});
    }

    if (metin(f.arama) !== "") {
        const oncekiSayi = satirlar.length;
        satirlar = applyAramaFiltresi(satirlar, f.arama);
        adimlar.push({anahtar: 'arama', etiket: 'Arama', elenen: oncekiSayi - satirlar.length});
    }

    return {satirlar: siralaOrijinal(satirlar), adimlar, toplam};
}

/**
 * Secili satirlarin gorunur kumeye dusen kismi.
 *
 * Secim BILEREK budanmiyor: kullanici bir filtreyle 3, baska bir filtreyle 2 dosya secip
 * hepsini birden isleme alabiliyor. DEV-2720'nin asil sorunu biriktirme degil, secimin
 * GORUNMEZ olmasiydi -- filtreyle gizlenen satirin isareti kaldirilamiyordu. Cozum: gizli
 * secim sayaca yansiyor ve "Secilenleri goster" ekranindan tek tek kaldirilabiliyor.
 */
export function kesistir(secilenler: Array<any>, gorunurler: Array<any>): Array<any> {
    if (!secilenler || secilenler.length === 0)
        return [];
    const gorunurIndexler = new Set(gorunurler.map((t: any) => t?.index));
    return secilenler.filter((s: any) => gorunurIndexler.has(s?.index));
}

/** Secime ekler; zaten secili olanlari tekrar eklemez, mevcut sirayi korur. */
export function secimeEkle(secilenler: Array<any>, eklenecekler: Array<any>): Array<any> {
    const mevcut = new Set((secilenler || []).map((s: any) => s?.index));
    return (secilenler || []).concat(
        (eklenecekler || []).filter((e: any) => !mevcut.has(e?.index))
    );
}

export function secimdenCikar(secilenler: Array<any>, cikarilacaklar: Array<any>): Array<any> {
    const cikacak = new Set((cikarilacaklar || []).map((c: any) => c?.index));
    return (secilenler || []).filter((s: any) => !cikacak.has(s?.index));
}

/**
 * Yuklenen listede hangi filtrelerin ANLAMI var? Excel ile gelen satirlarda acilis tarihi ve
 * dosya durumu bulunmadigi icin o filtreler hic gosterilmez -- kullanilamayan bir filtreyi
 * gosterip listeyi bosaltmaktansa hic gostermemek dogru. (DEV-2723)
 */
export function kullanilabilirFiltreler(takipler: Array<any> | undefined | null): Array<FiltreAnahtar> {
    const anahtarlar: Array<FiltreAnahtar> = ['birim', 'esasNo'];
    if (!takipler || takipler.length === 0)
        return anahtarlar;
    if (takipler.some((t: any) => !!t?.dosyaAcilisTarihi))
        anahtarlar.push('tarih');
    if (takipler.some((t: any) => metin(t?.dosyaDurum) !== ""))
        anahtarlar.push('dosyaDurum');
    return anahtarlar;
}

/** Secenekler yuklenen listeden turetilir: hicbir satirla eslesmeyen secenek gosterilmez. */
export function birimSecenekleri(takipler: Array<any> | undefined | null): Array<SecenekSatiri> {
    const harita = new Map<string, string>();
    (takipler || []).forEach((takip: any) => {
        const id = metin(takip?.birimId);
        if (id === "")
            return;
        if (!harita.has(id))
            harita.set(id, metin(takip?.eslesenBirimAdi) || metin(takip?.birimAdi) || id);
    });
    return Array.from(harita.entries())
        .map(([deger, metinDeger]) => ({deger, metin: metinDeger}))
        .sort((a, b) => a.metin.localeCompare(b.metin, 'tr'));
}

export function dosyaDurumSecenekleri(takipler: Array<any> | undefined | null): Array<SecenekSatiri> {
    const kume = new Set<string>();
    (takipler || []).forEach((takip: any) => {
        const durum = metin(takip?.dosyaDurum);
        if (durum !== "")
            kume.add(durum);
    });
    return Array.from(kume)
        .sort((a, b) => a.localeCompare(b, 'tr'))
        .map(durum => ({deger: durum, metin: durum}));
}

export function dosyaYilSecenekleri(takipler: Array<any> | undefined | null): Array<number> {
    const kume = new Set<number>();
    (takipler || []).forEach((takip: any) => {
        esasNoParcalari(takip?.dosyaNo).forEach(parca => kume.add(parca.yil));
    });
    return Array.from(kume).sort((a, b) => b - a);
}
